import { useState } from 'react';
import { t } from '@aquabox/shared';
import { ApiError } from '../api/client';
import { useTenantMutations } from '../api/queries';
import type { Region } from '../api/types';
import { useToast } from '../components/Toast';

interface AliasRow {
  key: string;
  target: string;
}

/** Sunucu 400 kodları → i18n. Bilinmeyen kod genel hataya düşer (ham metin gösterilmez). */
function saveErrorText(err: unknown): string {
  if (err instanceof ApiError && err.code === 'NEIGHBORHOOD_ALIAS_TARGET_UNKNOWN') {
    return t('tenant.settings.aliasTargetUnknown');
  }
  if (err instanceof ApiError && err.status === 403) {
    return t('tenant.settings.aliasesOwnerOnly');
  }
  const message = (err as { message?: string } | null)?.message;
  return message && message.length > 0 ? message : t('error.internal');
}

/** Panel adı karşılaştırma anahtarı (Türkçe büyük harf, boşluk sıkıştırılmış). */
function foldKey(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLocaleUpperCase('tr-TR');
}

/**
 * Bölgeler — "Mahalle takma adları" kartı (BA1-K). Panel adı (ör. "BAYSAL") → gerçek mahalle.
 *
 * Hedef yalnız mevcut mahalle listesinden seçilir: aktif bölgelerdeki mahalleler (sunucu daha geniş
 * bir harita kabul eder; panel kendi listesinden seçtirir). OWNER düzenler ve kaydeder; MANAGER
 * salt okunur görür. Kaydedilen tam harita PATCH /tenant/settings ile yazılır.
 */
export function NeighborhoodAliasesCard({
  isOwner,
  aliases,
  regions,
}: {
  isOwner: boolean;
  aliases: Record<string, string>;
  regions: Region[];
}) {
  const toast = useToast();
  const { updateSettings } = useTenantMutations();
  // Düzenleme taslağı; null = sunucudaki harita gösteriliyor.
  const [draft, setDraft] = useState<AliasRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const savedRows: AliasRow[] = Object.entries(aliases).map(([key, target]) => ({ key, target }));
  const rows = draft ?? savedRows;
  const targets = Array.from(new Set(regions.flatMap((r) => r.neighborhoods))).sort((a, b) =>
    a.localeCompare(b, 'tr'),
  );

  function updateRow(index: number, patch: Partial<AliasRow>): void {
    const base = draft ?? savedRows;
    setDraft(base.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function removeRow(index: number): void {
    const base = draft ?? savedRows;
    setDraft(base.filter((_, i) => i !== index));
  }

  function addRow(): void {
    setDraft([...(draft ?? savedRows), { key: '', target: '' }]);
  }

  async function save(): Promise<void> {
    setError(null);
    const current = draft ?? savedRows;
    if (current.some((r) => r.key.trim() === '' || r.target === '')) {
      setError(t('alias.keyRequired'));
      return;
    }
    const folded = current.map((r) => foldKey(r.key));
    if (new Set(folded).size !== folded.length) {
      setError(t('alias.duplicateKey'));
      return;
    }
    const map = Object.fromEntries(current.map((r) => [r.key.trim(), r.target]));
    try {
      await updateSettings.mutateAsync({ neighborhoodAliases: map });
      setDraft(null);
      toast(t('alias.saved'));
    } catch (err) {
      setError(saveErrorText(err));
    }
  }

  const dirty = draft !== null;

  return (
    <section
      data-testid="neighborhood-aliases-card"
      className="card"
      style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <strong style={{ fontSize: 15 }}>{t('alias.title')}</strong>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
        {t('alias.hint')}
      </p>
      {!isOwner ? (
        <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: 0 }}>
          {t('alias.readOnly')}
        </p>
      ) : (
        <p
          data-testid="alias-warning"
          style={{ fontSize: 12.5, color: 'var(--warning)', margin: 0 }}
        >
          {t('alias.warning')}
        </p>
      )}

      {rows.length === 0 ? (
        <p
          data-testid="alias-empty"
          style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}
        >
          {t('alias.empty')}
        </p>
      ) : null}

      {rows.map((row, index) =>
        isOwner ? (
          <div
            key={index}
            data-testid={`alias-row-${index}`}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1.2fr auto',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <input
              aria-label={t('alias.key')}
              placeholder={t('alias.keyPlaceholder')}
              value={row.key}
              onChange={(e) => updateRow(index, { key: e.target.value })}
              style={{ height: 38 }}
            />
            <select
              aria-label={t('alias.target')}
              value={row.target}
              onChange={(e) => updateRow(index, { target: e.target.value })}
              style={{ height: 38 }}
            >
              <option value="">{t('alias.target')}</option>
              {/* Kayıtlı hedef artık listede yoksa yine görünsün (kaybolup sessiz değişmesin). */}
              {row.target && !targets.includes(row.target) ? (
                <option value={row.target}>{row.target}</option>
              ) : null}
              {targets.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <button type="button" className="btn" onClick={() => removeRow(index)}>
              {t('alias.delete')}
            </button>
          </div>
        ) : (
          <div key={index} style={{ fontSize: 13 }}>
            <strong>{row.key}</strong> → {row.target}
          </div>
        ),
      )}

      {isOwner ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn"
            onClick={addRow}
            disabled={targets.length === 0 || updateSettings.isPending}
            title={targets.length === 0 ? t('alias.noTargets') : undefined}
          >
            {t('alias.add')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void save()}
            disabled={!dirty || updateSettings.isPending}
            data-testid="alias-save"
          >
            {t('alias.save')}
          </button>
        </div>
      ) : null}

      {error ? (
        <p role="alert" style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}>
          {error}
        </p>
      ) : null}
    </section>
  );
}
